import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import CurrencyBadge from '../Common/CurrencyBadge';
import { 
  UserIcon, 
  HomeIcon, 
  TruckIcon, 
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  XMarkIcon,
  MapIcon,
  CurrencyDollarIcon,
  CloudArrowUpIcon,
  GiftIcon,
  BellIcon,
  QuestionMarkCircleIcon,
  UserCircleIcon,
  ClockIcon,
  ChartBarIcon,
  UserGroupIcon,
  BellAlertIcon,
  ChatBubbleLeftRightIcon,
  ClipboardDocumentListIcon
} from '@heroicons/react/24/outline';

const Navbar: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (isMobileMenuOpen && !target.closest('nav')) {
        setIsMobileMenuOpen(false);
      }
    };

    if (isMobileMenuOpen) {
      // Add a small delay to avoid immediate closure on the same click that opened it
      setTimeout(() => {
        document.addEventListener('click', handleClickOutside);
      }, 100);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [isMobileMenuOpen]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const getDashboardLink = () => {
    switch (user?.role) {
      case 'customer':
        return '/customer-dashboard';
      case 'rider':
        return '/rider-dashboard';
      case 'merchant':
        return '/merchant-dashboard';
      case 'admin':
        return '/admin-dashboard';
      default:
        return '/';
    }
  };

  const getNavItems = () => {
    switch (user?.role) {
      case 'customer':
        return [
          { name: 'Home', href: '/customer-dashboard', icon: HomeIcon },
          { name: 'Bulk Upload', href: '/bulk-upload', icon: CloudArrowUpIcon },
          { name: 'Promo & Referrals', href: '/promo-referrals', icon: GiftIcon },
          { name: 'Notifications', href: '/notifications', icon: BellIcon },
          { name: 'Support', href: '/support', icon: QuestionMarkCircleIcon },
          { name: 'Profile', href: '/profile-settings', icon: UserCircleIcon },
        ];
      case 'rider':
        return [
          { name: 'Dashboard', href: '/rider-dashboard', icon: HomeIcon },
          { name: 'Available Jobs', href: '/available-jobs', icon: MapIcon },
          { name: 'My Jobs', href: '/my-jobs', icon: TruckIcon },
          { name: 'Earnings', href: '/earnings', icon: CurrencyDollarIcon },
          { name: 'Messages', href: '/rider-messages', icon: ChatBubbleLeftRightIcon },
          { name: 'Profile', href: '/rider-profile', icon: UserCircleIcon },
        ];
      case 'merchant':
        return [
          { name: 'Dashboard', href: '/merchant-dashboard', icon: HomeIcon },
          { name: 'Manage Agents', href: '/manage-agents', icon: UserIcon },
          { name: 'Orders', href: '/merchant-orders', icon: TruckIcon },
        ];
      case 'admin':
        return [
          { name: 'Dashboard', href: '/admin-dashboard', icon: HomeIcon },
          { name: 'Customers', href: '/admin/customers', icon: UserIcon },
          { name: 'Dispatchers', href: '/admin/dispatchers', icon: TruckIcon },
          { name: 'Orders', href: '/admin/orders', icon: ClipboardDocumentListIcon },
          { name: 'Withdrawals', href: '/admin/withdrawals', icon: CurrencyDollarIcon },
          { name: 'Settings', href: '/admin/settings', icon: Cog6ToothIcon },
        ];
      default:
        return [];
    }
  };

  const navItems = getNavItems();

  return (
    <nav className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <div className="flex-shrink-0">
            <Link to={getDashboardLink()} className="flex items-center">
              <img 
                src="/logo.png" 
                alt="Volexx Logistics" 
                className="h-14 w-auto md:h-16 object-contain"
              />
            </Link>
          </div>

          <div className="hidden md:flex md:items-center md:space-x-2">
            {navItems.map((item) => {
              const IconComponent = item.icon;
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={isActive ? 'nav-link-active' : 'nav-link'}
                >
                  <IconComponent className="h-4 w-4 mr-2" />
                  <span className="hidden lg:block">{item.name}</span>
                </Link>
              );
            })}
          </div>

          <div className="hidden md:flex md:items-center md:space-x-4">
            <CurrencyBadge compact />
            <div className="text-right">
              <div className="text-sm font-semibold text-gray-900">
                {user?.name}
              </div>
              <div className="text-xs text-gray-500 capitalize">
                {user?.role}
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="flex items-center px-4 py-2 text-sm font-semibold text-white bg-gradient-to-r from-red-500 to-red-600 rounded-volexx hover:from-red-600 hover:to-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-all duration-300 hover:scale-105 hover:shadow-lg active:scale-95"
            >
              <ArrowRightOnRectangleIcon className="h-5 w-5 mr-2" />
              <span className="hidden lg:block">Logout</span>
            </button>
          </div>

          <div className="md:hidden">
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="inline-flex items-center justify-center p-2 rounded-volexx text-gray-600 hover:text-gray-900 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-volexx-blue-500 transition-all duration-200"
              aria-expanded="false"
            >
              <span className="sr-only">Open main menu</span>
              {isMobileMenuOpen ? (
                <XMarkIcon className="h-6 w-6" />
              ) : (
                <Bars3Icon className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div className="md:hidden border-t border-gray-200 bg-white">
          <div className="px-2 pt-2 pb-3 space-y-1">
            {navItems.map((item) => {
              const IconComponent = item.icon;
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`flex items-center px-3 py-3 text-base font-medium rounded-volexx transition-all duration-200 ${
                    isActive 
                      ? 'text-volexx-blue-600 bg-volexx-blue-50' 
                      : 'text-gray-700 hover:text-volexx-blue-600 hover:bg-gray-50'
                  }`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <IconComponent className="h-5 w-5 mr-3 flex-shrink-0" />
                  {item.name}
                </Link>
              );
            })}
          </div>
          
          <div className="px-2 py-3 border-t border-gray-200 bg-gray-50">
            <div className="flex items-center px-3 py-2">
              <div className="flex-shrink-0">
                <div className="h-10 w-10 rounded-full bg-gradient-blue flex items-center justify-center">
                  <span className="text-white font-semibold text-sm">
                    {user?.name?.charAt(0).toUpperCase()}
                  </span>
                </div>
              </div>
              <div className="ml-3 flex-1">
                <div className="text-base font-semibold text-gray-900">{user?.name}</div>
                <div className="text-sm text-gray-600 capitalize">{user?.role}</div>
              </div>
            </div>
            <div className="mt-3 px-3">
              <label className="block text-xs font-semibold text-gray-600 mb-1.5 font-poppins">Currency</label>
              <CurrencyBadge />
            </div>
            <div className="mt-2">
              <button
                onClick={() => {
                  handleLogout();
                  setIsMobileMenuOpen(false);
                }}
                className="flex items-center w-full px-4 py-3 text-base font-semibold text-white bg-gradient-to-r from-red-500 to-red-600 rounded-volexx hover:from-red-600 hover:to-red-700 transition-all duration-300 hover:shadow-lg"
              >
                <ArrowRightOnRectangleIcon className="h-5 w-5 mr-3 flex-shrink-0" />
                Logout
              </button>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
